import { type Dispatch, type FC, type SetStateAction } from "react";

import { Box, Slider, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEditCampaign } from "../../../context/EditCampaignContext";

interface BrandingDefaultSettingsProps {
  onTextColor: Dispatch<SetStateAction<string | undefined>>;
  onBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  onBorderRadius: Dispatch<SetStateAction<number | undefined>>;
  onBackgroundOpacity: Dispatch<SetStateAction<number | undefined>>;
  textColor?: string;
  backgroundColor?: string;
  borderRadius?: number;
  backgroundOpacity?: number;
}

export const BrandingDefaultSettings: FC<BrandingDefaultSettingsProps> = ({
  onTextColor,
  onBackgroundColor,
  onBorderRadius,
  onBackgroundOpacity,
  backgroundColor,
  textColor,
  backgroundOpacity,
  borderRadius,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { setDetectedChange } = useEditCampaign();

  const sliderStyles = (disabled: boolean) => {
    return {
      color: "#FFCF96",
      "& .MuiSlider-track": {
        border: "none",
        color: disabled ? theme.palette.natural.t3 : undefined,
      },
      "& .MuiSlider-thumb": {
        width: 20,
        height: 20,
        borderRadius: 0,
        background: disabled
          ? theme.palette.natural.t3
          : theme.palette.main.gradient.primary.p2,
        "&:before": {
          boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
        },
        "&:hover, &.Mui-focusVisible, &.Mui-active": {
          boxShadow: "none",
        },
      },
    };
  };

  return (
    <Stack direction='column' gap={1}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170 }}>
          {t("campaign.branding.defaultSettings.backgroundColor")}:
        </Typography>
        <input
          defaultValue='#ffffff'
          type='color'
          style={{
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
            border: "none",
            width: 100,
            height: 30,
            cursor: "pointer",
            padding: 0,
            backgroundColor: "transparent",
          }}
          value={backgroundColor ?? "#ffffff"}
          onChange={e => {
            onBackgroundColor(e.currentTarget.value);
            setDetectedChange(true);
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170 }}>
          {t("campaign.branding.defaultSettings.textColor")}:
        </Typography>
        <input
          type='color'
          style={{
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
            border: "none",
            width: 100,
            height: 30,
            cursor: "pointer",
            padding: 0,
            backgroundColor: "transparent",
          }}
          value={textColor ?? "#000000"}
          onChange={e => {
            onTextColor(e.currentTarget.value);
            setDetectedChange(true);
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "30px",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170, textWrap: "nowrap" }}>
          {t("campaign.branding.defaultSettings.backgroundOpacity")}:
        </Typography>
        <Slider
          aria-label='Volume'
          sx={{
            ...sliderStyles(false),
            maxWidth: 200,
          }}
          min={0}
          max={1}
          step={0.1}
          defaultValue={backgroundOpacity ?? 1}
          value={backgroundOpacity ?? 1}
          onChange={(_, value) => {
            onBackgroundOpacity(value as number);
            setDetectedChange(true);
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "30px",
        }}
      >
        <Typography sx={{ fontSize: 17, width: 170, textWrap: "nowrap" }}>
          {t("campaign.branding.defaultSettings.borderRadius")}:
        </Typography>
        <Slider
          aria-label='Border radius'
          sx={{
            ...sliderStyles(false),
            maxWidth: 200,
          }}
          min={0}
          step={1}
          max={30}
          value={borderRadius ?? 0}
          onChange={(_, value) => {
            onBorderRadius(value as number);
            setDetectedChange(true);
          }}
        />
      </Box>
    </Stack>
  );
};
